import { readableColor, rem } from 'polished';
import React, { SyntheticEvent } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';
import { blue, red, grayscale, slate, white, black } from '@vimeo/iris/color';
import { Button } from '@vimeo/iris/components';
import { CircleFilled, DownloadArrow, Play } from '@vimeo/iris/icons';
import { Header, Paragraph } from '@vimeo/iris/typography';

import { AlbumThemes, Pictures } from '../../types';
import { getTimecodeFromSeconds } from '../../utils/helpers';

type Props = {
  brandColor: string;
  title: string;
  description: string;
  duration: number;
  thumbnails: Pictures;
  theme: AlbumThemes;
  onPlay: VoidFunction;
  type: string;
  live?: {
    status: string;
  };
  allowDownloads: boolean;
  setDownloadClipId?: (id: number) => void;
  openDownloadModal?: () => void;
  displayType: string;
  videoId: number;
};

const LiveGridItem = ({
  brandColor,
  description,
  duration,
  onPlay,
  theme,
  thumbnails,
  title,
  type,
  live,
  allowDownloads,
  setDownloadClipId,
  openDownloadModal,
  displayType,
  videoId,
}: Props) => {
  const thumbnail = thumbnails.sizes[0];

  // image base URL
  const thumbnailURL = thumbnail.link.substring(0, thumbnail.link.lastIndexOf('_'));

  // image fallback:
  const thumbnailSrc = thumbnail && thumbnail.link ? `${thumbnailURL}_640x350` : undefined;

  // responsive images
  const thumbnailSrcSet = `${thumbnailURL}_640x350 640w, ${thumbnailURL}_960x525 960w, ${thumbnailURL}_1280x700 1280w`;

  const isArchivedOrNonLive = type !== 'live' || !live || live.status === 'done';
  const isStreaming = live && live.status === 'streaming';
  const isUpcoming =
    live && (live.status === 'unavailable' || live.status === 'pending' || live.status === 'ready');

  return (
    <Item>
      <ThumbContainer onClick={onPlay}>
        <PlayIcon brandColor={brandColor} />
        <ThumbImg src={thumbnailSrc} srcSet={thumbnailSrcSet} alt={title} />
        {isArchivedOrNonLive && <Badge>{getTimecodeFromSeconds(duration, 'mm:ss')}</Badge>}
        {allowDownloads && isArchivedOrNonLive && (
          <DownloadButtonContainer title={translate('Download')}>
            <Button
              icon={<DownloadArrow />}
              format="basic"
              variant="hyperminimal"
              size="md"
              onClick={(e: SyntheticEvent) => {
                e.stopPropagation();
                if (displayType !== 'CLIP_PLACEHOLDER' && setDownloadClipId && openDownloadModal) {
                  setDownloadClipId(videoId);
                  openDownloadModal();
                }
              }}
            />
          </DownloadButtonContainer>
        )}
        {isStreaming && (
          <StreamingBadge>
            <RecordIcon /> LIVE
          </StreamingBadge>
        )}
        {isUpcoming && <Badge>UPCOMING</Badge>}
      </ThumbContainer>
      <div>
        <TitleWrapper title={title} onClick={onPlay}>
          <Title albumTheme={theme}>{title}</Title>
        </TitleWrapper>
        {description && <Description albumTheme={theme}>{description}</Description>}
      </div>
    </Item>
  );
};

const Item = styled.div`
  flex: 0 1 ${rem(280)};
  display: block;
  width: 100%;
  padding: ${rem(10)} ${rem(10)} ${rem(20)};
  position: relative;
  overflow: hidden;
`;

const Badge = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  position: absolute;
  right: ${rem(15)};
  bottom: ${rem(15)};
  padding: ${rem(2)} ${rem(8)};
  font-size: ${rem(12)};
  font-weight: normal;
  border-radius: ${rem(2)};
`;

const ThumbContainer = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  max-height: ${rem(200)};
  text-align: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: black;

  &::before {
    content: ' ';
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0);
    transition: background 0.2s ease-out;
  }

  &:hover {
    & > svg {
      opacity: 1;
    }

    &::before {
      background: rgba(0, 0, 0, 0.4);
    }
  }
`;

const ThumbImg = styled.img`
  width: auto;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
`;

const TitleWrapper = styled.div`
  margin: ${rem(10)} 0 0;
  cursor: pointer;
`;

const Title = styled(Header).attrs({ size: '5' })<{ albumTheme: AlbumThemes }>`
  display: block;
  display: -webkit-box;
  overflow: hidden;
  margin-bottom: ${rem(5)};
  max-height: ${rem(39)};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: ${(props) => (props.albumTheme === 'dark' ? white : black)};
`;

const Description = styled(Paragraph).attrs({ size: '2' })<{ albumTheme: AlbumThemes }>`
  display: block;
  display: -webkit-box;
  margin-bottom: 0;
  font-weight: normal;
  max-height: ${rem(38)};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${(props) => (props.albumTheme === 'dark' ? slate(200) : grayscale(500))};
`;

const StreamingBadge = styled(Badge)`
  background-color: ${red(500)};
`;

const RecordIcon = styled(CircleFilled)`
  width: ${rem(7)};
  height: ${rem(7)};
  margin-bottom: ${rem(1)};
  circle {
    fill: white;
  }
`;

const DownloadButtonContainer = styled.span`
  position: absolute;
  bottom: ${rem(10)};
  left: ${rem(10)};
  z-index: 3;
`;

const PlayIcon = styled(Play)<{ brandColor: string }>`
  background: ${(props) => (props.brandColor ? props.brandColor : blue(500))};
  padding: ${rem(8)} ${rem(20)};
  z-index: 2;
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  width: ${rem(70)};
  border-radius: ${rem(5)};
  max-height: ${rem(46)};
  transition: opacity 0.2s ease-out;

  path {
    fill: ${(props) =>
      props.brandColor ? readableColor(props.brandColor) : readableColor(blue(500))};
  }
`;

export default LiveGridItem;
